import { useUserOnboarding } from "@/api/onboarding";
import { usePrivateWorkspace } from "@/api/workspaces";
import { useRegisterWorkspaceDetailDropContext } from "@/dnd";
import { Flags } from "@/providers/LaunchDarklyProvider";
import {
  TemplateCopyPromptCard,
  useTemplateCopyPromptContext,
} from "@/template-prompt";
import {
  type Canvas,
  type FileRecord,
  type InboxItem,
} from "@cohesive/api-client";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useState } from "react";
import { FinishOnboardingCard } from "../../components/FinishOnboardingCard";
import { useSelectionKeyboard } from "../../hooks/useSelectionKeyboard";
import { useWorkspaceSelection } from "../../hooks/useWorkspaceSelection";
import { useHomeBrowserData } from "../hooks/useHomeBrowserData";
import { useHomeBrowserState } from "../hooks/useHomeBrowserState";
import { useHomeViewMode } from "../hooks/useHomeViewMode";
import {
  browserItemSelectionId,
  HOME_BROWSER_LIMIT,
} from "../utils/homeBrowserTypes";
import { homeEmptyCopy } from "../utils/homeEmptyCopy";
import { sortBrowserItems } from "../utils/homeSort";
import { HomeBrowserHeader } from "./HomeBrowserHeader";
import { HomeBrowserList } from "./HomeBrowserList";
import { NewMenu } from "./NewMenu";
import { useBrowserItemRenderers } from "./useBrowserItemRenderers";
import { useBrowserSelectionBar } from "./useBrowserSelectionBar";
import { useHomeCreateActions } from "./useHomeCreateActions";

/**
 * Card grid runs 3-up at the home column's full width: the default 286px track
 * only fits 2, so the browser sets a denser track through the shared
 * `--ws-card-track` knob. Still auto-fill — it reflows to 2 / 1 as the
 * viewport or an open right pane narrows the column, and packs more when the
 * wide-layout toggle grows the column. 270px keeps 3-up inside the 58rem
 * surface (856px content after padding, minus two 16px gaps) with a little
 * slack before a 4th column would fit.
 */
const HOME_CARD_GRID_TRACK_PX = 270;

export interface HomeBrowserProps {
  /** Rendered as the first card/row before the items. */
  leadingSlot?: React.ReactNode;
  /** File records already represented by an upload ghost; hidden to avoid dupes. */
  suppressedRecordIds?: Set<string>;
  /** Wide-layout toggle state, owned by the surface (it controls the column). */
  wide?: boolean;
  onWideChange?: (wide: boolean) => void;
}

/**
 * The org-wide home browser: a tab strip (with the view/sort/new cluster)
 * above a grid/list of recent resources that floats on the page background.
 * Cards and rows use the page click model — single click selects, double
 * click opens (see `useWorkspaceSelection`). The workspace detail page
 * renders its own `WorkspaceBrowser` instead.
 */
export function HomeBrowser({
  leadingSlot,
  suppressedRecordIds,
  wide,
  onWideChange,
}: HomeBrowserProps = {}) {
  const selection = useWorkspaceSelection();

  // Home lands on Canvases — the strip's first tab (no mixed view here).
  const { dispatch, tab, sort } = useHomeBrowserState("canvases");
  const [view, setView] = useHomeViewMode(tab);

  // Only the active tab's queries are enabled; the others keep their caches.
  const data = useHomeBrowserData(tab);

  // Finish-onboarding CTA — rendered as the leading card in the All tab (the
  // landing view) for users who skipped or are mid-onboarding (until
  // dismissed), rather than a large card under the composer. `setComplete` lags
  // the cached status, so a local flag hides it immediately on dismiss.
  const { data: onboarding } = useUserOnboarding({ enabled: true });
  const flags = useFlags<Flags>();
  const [finishOnboardingDismissed, setFinishOnboardingDismissed] =
    useState(false);
  // Only home mounts the provider, so the workspace detail page's browser
  // never sees a template card.
  const showTemplateCard = useTemplateCopyPromptContext()?.view === "card";
  const showFinishOnboardingCard =
    !!flags.onboarding &&
    (onboarding?.global === "skipped" ||
      onboarding?.global === "in_progress") &&
    !finishOnboardingDismissed;

  // Merged tabs (All / Starred) can exceed the per-source page size, so cap the
  // displayed list at HOME_BROWSER_LIMIT total after the chronological sort.
  const items = useMemo(() => {
    const visible = data.items.filter((item) => {
      // A file already drawn as an upload ghost must not also draw as a card.
      if (
        suppressedRecordIds?.size &&
        item.kind === "file" &&
        suppressedRecordIds.has(item.data.id)
      ) {
        return false;
      }
      return true;
    });
    return sortBrowserItems(visible, sort).slice(0, HOME_BROWSER_LIMIT);
  }, [data.items, sort, suppressedRecordIds]);

  // Derived from the unsorted list so toggling sort doesn't churn the
  // permission-check / unique-name inputs of the action hooks.
  const canvasesInScope = useMemo(
    () =>
      data.items.flatMap((item): Canvas[] =>
        item.kind === "canvas" ? [item.data] : [],
      ),
    [data.items],
  );
  const filesInScope = useMemo(
    () =>
      data.items.flatMap((item): FileRecord[] =>
        item.kind === "file" ? [item.data] : [],
      ),
    [data.items],
  );

  // Drag payloads resolve records through this context (same as the detail
  // page). Provenance comes from each record itself; the private workspace is
  // only the fallback for records missing one — home items span workspaces,
  // so no single id fits.
  const privateWorkspace = usePrivateWorkspace();
  const canvasMap = useMemo(() => {
    const m = new Map<string, Canvas>();
    for (const c of canvasesInScope) m.set(c.id, c);
    return m;
  }, [canvasesInScope]);
  const fileRecordMap = useMemo(() => {
    const m = new Map<string, FileRecord>();
    for (const f of filesInScope) m.set(f.id, f);
    return m;
  }, [filesInScope]);
  const inboxItemMap = useMemo(() => {
    const m = new Map<string, InboxItem>();
    for (const item of data.items) {
      if (item.kind === "chat") m.set(item.data.id, item.data);
    }
    return m;
  }, [data.items]);
  useRegisterWorkspaceDetailDropContext(
    useMemo(
      () =>
        privateWorkspace
          ? {
              sourceWorkspaceId: privateWorkspace.id,
              canvasMap,
              fileRecordMap,
              inboxItemMap,
            }
          : null,
      [privateWorkspace, canvasMap, fileRecordMap, inboxItemMap],
    ),
  );

  const { renderGridItem, renderRow, canvasActions, fileActions, chatActions } =
    useBrowserItemRenderers({
      canvasesInScope,
      filesInScope,
      // Cards drag like the workspace page's — into the composer to attach,
      // onto rail targets to file/star.
      draggable: true,
      // No folders on home (they're filtered out of every tab here).
      onFolderOpen: () => {},
    });

  // Shift-range selection walks rows in display order.
  const orderedSelectionIds = useMemo(
    () =>
      items
        .map(browserItemSelectionId)
        .filter((id): id is string => id !== null),
    [items],
  );
  useEffect(() => {
    selection.setOrderedIds(orderedSelectionIds);
  }, [orderedSelectionIds, selection]);

  const { selectionBar, openSelectedId } = useBrowserSelectionBar({
    items: data.items,
    allRecords: filesInScope,
    canvasActions,
    fileActions,
    openChat: chatActions.open,
  });
  useSelectionKeyboard(openSelectedId);

  // ----- "+ New" ----- shared with the page's right-click menu.
  const createActions = useHomeCreateActions();

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-medium text-n-90">Recent Activity</h2>
        {selectionBar}
      </div>
      <HomeBrowserHeader
        tab={tab}
        onTabChange={(next) => dispatch({ type: "pickTab", tab: next })}
        view={view}
        onViewChange={setView}
        sort={sort}
        onSortChange={(next) => dispatch({ type: "setSort", sort: next })}
        wide={wide}
        onWideChange={onWideChange}
        newMenu={<NewMenu {...createActions} />}
      />
      <HomeBrowserList
        view={view}
        items={items}
        isPending={data.isPending}
        isError={data.isError}
        onRetry={data.refetch}
        empty={homeEmptyCopy(tab)}
        renderGridItem={renderGridItem}
        renderRow={renderRow}
        gridTrackPx={HOME_CARD_GRID_TRACK_PX}
        sort={sort}
        onSortFieldChange={(field) => dispatch({ type: "sortByField", field })}
        /* One leading slot: the caller's, else the deferred template prompt,
           else the onboarding CTA. */
        leadingSlot={
          leadingSlot ??
          (tab === "canvases" ? (
            showTemplateCard ? (
              <TemplateCopyPromptCard
                variant={view === "list" ? "row" : "card"}
              />
            ) : showFinishOnboardingCard ? (
              <FinishOnboardingCard
                variant={view === "list" ? "row" : "card"}
                onDismiss={() => setFinishOnboardingDismissed(true)}
              />
            ) : undefined
          ) : undefined)
        }
      />
    </section>
  );
}
